import type { TradeItem } from '@/types';
import type { MtmRow, MtmAggregates } from '@/types';
import { resolveDefaultPrice } from './mtm-cell-helpers';
import { getEffectiveSettlementCurrency, toSettlementValue } from './settlement-ccy';

/**
 * Recompute a single MTM row from the effective price.
 *
 * `margin` on TradeItem is a PERCENTAGE (e.g. 105 = 105%), per the backend
 * Position.margin property (margin_bps / 100). The margined value is
 * quantity × price × (margin / 100) — matching backend calculate_margin_price
 * (gross_price × margin_decimal) × quantity. The margin PERCENT is fixed; the
 * margined VALUE is what recomputes when price changes. See 03-01-PLAN.md
 * margin_formula_resolution for the full rationale.
 *
 * Precision: delegates to marginValueCents — a single float-multiply rounded ONCE to
 * the nearest cent. The price is NOT pre-rounded to cents, so its full 4-decimal API
 * precision (decimal_places=4) survives into the value (153.7575 × 1000 = 153,757.50,
 * not 153,760.00). The single integer-cent result is then safe to accumulate across rows.
 *
 * Worked example: quantity=1000, margin=105, price=152.00
 *   valueCents = Math.round(1000 × 152.00 × 105) = 15_960_000 → '159600.00'
 */
export function computeMtmRow(
  position: TradeItem,
  overridePrice: string | undefined,
): MtmRow {
  // SINGLE SOURCE OF TRUTH: resolve via getMtmSubmitPrice (override ?? cob ?? gross)
  // so the aggregate-bar margin totals use the SAME effective price as the net
  // exposure cell beside them, the Run-MTM dialog "After" total, and the submitted
  // marks[]. The prefill chain reads cob_price_raw (T-1 close, LR-393), no longer
  // latest_mark_price_raw — which was cyclic (re-mark vs prior mark, not vs market).
  const effectivePrice = getMtmSubmitPrice(position, overridePrice);
  // Margined value via the shared helper (single float-multiply, round once to cents) —
  // byte-identical to baselineMarginedCents and the Run-MTM dialog, so they cannot drift.
  // LR-388: effectiveQuantity = remaining markable shape (booked − pending recall).
  const valueCents = marginValueCents(effectiveQuantity(position), effectivePrice, position.margin);
  return {
    id: position.id,
    trade_type: position.trade_type,
    effectivePrice,
    recomputedMargin: (valueCents / 100).toFixed(2),
  };
}

type MtmBucket = 'lent' | 'borrowed' | 'repo' | 'reverse';
const BUCKET_BY_TYPE: Record<TradeItem['trade_type'], MtmBucket> = {
  lend: 'lent',
  borrow: 'borrowed',
  repo: 'repo',
  reverse_repo: 'reverse',
};

/**
 * Sum per-bucket margined values across rows, excluding rows in excludedIds.
 * Sums in integer cents to avoid float drift across a large position set;
 * formats each bucket to a 2-decimal string once at the end.
 * NO net exposure this phase (Phase 5, Natallie-gated).
 */
export function computeMtmAggregates(
  rows: MtmRow[],
  excludedIds: Set<number>,
): MtmAggregates {
  const cents: Record<'lent' | 'borrowed' | 'repo' | 'reverse', number> = {
    lent: 0,
    borrowed: 0,
    repo: 0,
    reverse: 0,
  };
  for (const row of rows) {
    if (excludedIds.has(row.id)) continue;
    const bucket = BUCKET_BY_TYPE[row.trade_type];
    // Parse recomputedMargin back to cents for accumulation — avoids float drift in multi-row sums
    cents[bucket] += Math.round(Number(row.recomputedMargin) * 100);
  }
  return {
    lent: (cents.lent / 100).toFixed(2),
    borrowed: (cents.borrowed / 100).toFixed(2),
    repo: (cents.repo / 100).toFixed(2),
    reverse: (cents.reverse / 100).toFixed(2),
  };
}

// ─── Phase 5: Net Exposure ───────────────────────────────────────────────────

function getMtmEffectivePrice(t: TradeItem, priceOverrides: Map<number, string>): string {
  // SINGLE SOURCE OF TRUTH: delegate to getMtmSubmitPrice so exposure and the
  // submit/dialog path share exactly one fallback chain
  // (override ?? resolveDefaultPrice: cob_price_raw ?? gross_price_raw — LR-393).
  return getMtmSubmitPrice(t, priceOverrides.get(t.id));
}

export interface MtmExposure {
  /**
   * Stock Lend/Borrow net exposure SPLIT PER CURRENCY (LR-387), keyed by the
   * security/pricing currency (the currency the margined value is denominated in).
   * Raw numbers for formatCurrency; negative = against us.
   * Trades priced in different currencies are NEVER summed together — a raw
   * cross-currency sum (e.g. USD + EUR) is meaningless.
   */
  stockLBNetByCcy: Record<string, number>;
  /**
   * Always `{}`. Repo/reverse_repo exposure is owned by LR-385 (per-counterparty
   * cash-pool margin call) and is not computed here, so it never contributes a
   * (possibly cross-currency) figure to this bar.
   */
  repoNetByCcy: Record<string, number>;
}

/**
 * Net exposure per group, included rows only, SPLIT PER CURRENCY (LR-387). Sign
 * convention: + in our favour (CP pays us), − against us (we pay CP).
 *
 * Per-trade = signed Δ MARGINED value against the MOVING baseline:
 *   lend:   newMarginedValue − baselineMarginedValue
 *   borrow: baselineMarginedValue − newMarginedValue
 * baselineMarginedValue is the margined value at the LAST MARKED price, or — for a
 * position never marked — at the gross/booking price (mtmBaselinePriceRaw). This makes
 * the signed net the INCREMENTAL margin call vs the previous mark, not the cumulative P&L
 * since booking: after a run the new mark becomes the next baseline automatically. Per
 * Tim [01:18:56] ("от предыдущего раза, когда сделали MTM или забили трейд в первый раз")
 * and Exantech §2e ("last marked cash amount, or if no marks, original contract value").
 * newMarginedValue is the margined integer-cent value via marginValueCents
 * (quantity × effectivePrice × margin/100). Both sides use the same helper, so the bar
 * and the Run-MTM dialog can never disagree; correct at haircut ≠ 100%.
 *
 * CURRENCY (LR-387): per the "Repricing - MtM.xlsx" Key Takeaways, trades priced in
 * different currencies are SPLIT OUT PER CURRENCY (by agreement with cp) rather than
 * summed. We accumulate exposureCents into a per-`currency` bucket. This is a pure
 * GROUPING change — the per-trade math and the LR-368 moving baseline are untouched,
 * so each currency's net is the same incremental call it always was, just not mixed
 * with the others. (A future "convert to an agreed base currency" mode would need the
 * per-trade settlement FX rate; the split path here keys on `currency` and needs none.)
 *
 * Verified against the customer "Repricing - MtM.xlsx" Scenario 2 (Goldman Sachs basket,
 * single-currency USD, margin 100%, FIRST mark ⇒ baseline = booking): AAPL lend +5,000,
 * INTC lend −20,000, NUGT borrow −48,000 → net { USD: −63,000 }, i.e. due to CP. (CSV is the
 * authoritative customer source.)
 *
 * Repo/reverse_repo exposure is owned by LR-385 and is NOT computed here.
 *
 * Float-safe: per-currency integer-cent accumulation, divide once at the end.
 */
export function computeMtmExposure(
  rows: TradeItem[],
  priceOverrides: Map<number, string>,
  excludedIds: Set<number>,
): MtmExposure {
  // Per-currency integer-cent accumulators for the Stock L/B bucket.
  const stockLBCents: Record<string, number> = {};
  for (const t of rows) {
    if (excludedIds.has(t.id)) continue;
    // Only lend/borrow contribute. repo/reverse_repo exposure is owned by LR-385
    // (per-counterparty cash-pool margin call), so it never enters repoNetByCcy.
    if (t.trade_type !== 'lend' && t.trade_type !== 'borrow') continue;
    const effectivePriceStr = getMtmEffectivePrice(t, priceOverrides);
    // Margined NEW value (integer cents) via the shared helper — NOT gross priceCents × quantity,
    // so the figure stays correct at margin/haircut ≠ 100%. LR-388: effectiveQuantity = remaining
    // markable shape (booked − pending recall), so a pending partial recall reduces the exposure.
    const newMarginedCents = marginValueCents(effectiveQuantity(t), effectivePriceStr, t.margin);
    // Moving baseline: margined value at the last marked price, or gross/booking for a
    // never-marked position. NOT the frozen contract_value — that made every repeat mark
    // re-measure from booking (cumulative), overstating the day's incremental call.
    const baselineCents = baselineMarginedCents(t);
    const exposureCents =
      t.trade_type === 'lend'
        ? newMarginedCents - baselineCents
        : baselineCents - newMarginedCents;
    // Split per currency — LR-474 (D2): accumulate under the trade's EFFECTIVE
    // SETTLEMENT currency, converting the security-ccy cents at the BOOKED
    // fx_rate (linear, so cents convert directly). A cross-ccy trade without a
    // usable fx_rate stays honestly under its security ccy. Keys are uppercased
    // (pre-existing mismatch vs the pill/aggregateByCurrency, fixed here).
    const conv = toSettlementValue(t, exposureCents);
    const ccy =
      (conv.converted ? getEffectiveSettlementCurrency(t) : (t.currency || '').toUpperCase()) || 'USD';
    stockLBCents[ccy] = (stockLBCents[ccy] ?? 0) + Math.round(conv.value);
  }
  const stockLBNetByCcy: Record<string, number> = {};
  for (const [ccy, cents] of Object.entries(stockLBCents)) {
    stockLBNetByCcy[ccy] = cents / 100;
  }
  return {
    stockLBNetByCcy,
    // Repo bucket is owned by LR-385 — intentionally empty (renders as "—").
    repoNetByCcy: {},
  };
}

// ─── LR-389: Minimum Transfer Amount (MTA) gate ──────────────────────────────

export interface MtaGate {
  /** Configured threshold magnitude, or null when no MTA is set for the counterparty. */
  mta: number | null;
  /** The signed net exposure (+ = due to us, − = due to CP) that was gated. */
  net: number;
  /** True when an MTA is configured AND |net| < MTA → transfer is NOT initiated. */
  belowMta: boolean;
  /** Amount that would actually transfer: net when at/above the threshold, else 0. */
  postingAmount: number;
}

/**
 * Gate a net exposure (+ = due to us, − = due to CP) by the counterparty's minimum
 * transfer amount (MTA).
 *
 * Per "Repricing - MtM.xlsx" Scenario 2, a margin-call transfer is initiated only when the
 * MAGNITUDE of net exposure reaches the agreed MTA: `|net| >= MTA` → transfer the full signed
 * `net`; `|net| < MTA` → "below MTA", nothing transfers (postingAmount 0). The boundary
 * `|net| === MTA` transfers (≥, not >). A null MTA means no threshold is configured, so the
 * gate is inert (never belowMta, always posts net).
 *
 * Soft gate (LR-389 decision): this only governs the displayed transfer amount + indicator —
 * it does NOT block the MTM run itself (price marks still post and move the baseline). Compares
 * in integer cents to stay consistent with the float-safe discipline of the rest of this module.
 * Currency: net and MTA are assumed to be in the same currency (USD in the prototype); no FX
 * conversion is applied (out of scope).
 */
export function evaluateMtaGate(net: number, mta: number | null | undefined): MtaGate {
  if (mta == null) {
    return { mta: null, net, belowMta: false, postingAmount: net };
  }
  const belowMta = Math.round(Math.abs(net) * 100) < Math.round(mta * 100);
  return { mta, net, belowMta, postingAmount: belowMta ? 0 : net };
}

/**
 * Resolve the currency symbol for an ISO 4217 code (e.g. 'USD' → '$', 'EUR' → '€',
 * 'GBP' → '£', 'JPY' → '¥'). Falls back to the raw code for an unknown/invalid code
 * (Intl throws a RangeError on a bad currency) so the formatter never crashes.
 */
function currencySymbol(currency: string): string {
  try {
    const parts = new Intl.NumberFormat('en-US', { style: 'currency', currency }).formatToParts(0);
    return parts.find((p) => p.type === 'currency')?.value ?? currency;
  } catch {
    return currency;
  }
}

/**
 * Compact signed currency: ±<sym>X.XM / ±<sym>X.XK / ±<sym>X. Re-created from LR-104
 * MetricsBar. `currency` defaults to USD (LR-387), so every existing single-arg call
 * site is byte-identical; pass an ISO code to render the matching symbol instead of a
 * hardcoded "$" (LR-387: the per-currency MTM exposure cells pass the bucket's ISO code).
 *
 * NOTE — distinct from `utils/format.ts#formatCurrency`, which has the SAME signature but
 * renders the FULL Intl currency form ("$1,376,550.00"). THIS one is the COMPACT K/M
 * variant used across the MTM bars; import deliberately, they are not interchangeable.
 */
export function formatCurrency(value: number, currency = 'USD'): string {
  const abs = Math.abs(value);
  const sign = value < 0 ? '-' : '';
  const sym = currencySymbol(currency);
  if (abs >= 1_000_000) {
    const formatted = new Intl.NumberFormat('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(abs / 1_000_000);
    return `${sign}${sym}${formatted}M`;
  }
  if (abs >= 1_000) {
    const formatted = new Intl.NumberFormat('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(abs / 1_000);
    return `${sign}${sym}${formatted}K`;
  }
  const formatted = new Intl.NumberFormat('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(abs);
  return `${sign}${sym}${formatted}`;
}

/**
 * Full-precision currency: always two decimal places, grouped thousands
 * (e.g. 159600 → "$159,600.00"). Use this for the Run-MTM confirmation dialog,
 * where the user must see the EXACT figure being committed — never the compact
 * `formatCurrency` (which rounds to $X.XK / $X.XM / whole dollars and can render
 * two materially different deltas identically). The signed +/- prefix is applied
 * by the caller (the dialog), so this formatter intentionally does NOT add a sign.
 * `currency` defaults to USD so legacy call sites are byte-identical; pass the
 * figure's real ISO code (LR-474 — the '$' on EUR/GBP amounts was a lie).
 * Falls back to "<n> <CODE>" on an invalid code (Intl throws RangeError).
 */
export function formatCurrencyExact(value: number, currency = 'USD'): string {
  // Sub-cent band → an unsigned zero: Intl would render -0 / "-0.004" as
  // "-$0.00", putting a minus on a figure whose displayed magnitude is zero.
  // Same 0.005 threshold as the dialog's isSettled gate (LR-518 kvi review #2).
  const v = Math.abs(value) < 0.005 ? 0 : value;
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(v);
  } catch {
    const n = new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(v);
    return `${n} ${currency}`;
  }
}

/**
 * Signed currency tagged with an explicit ISO code instead of a "$" symbol, full
 * grouped thousands (e.g. -63000 → "-63,000 USD", 5000 → "+5,000 USD"). Used by the
 * MTM run history (run net + per-instrument deltas), where the run's currency is known
 * and may not be USD — unlike `formatCurrency`/`formatCurrencyExact`, which hardcode "$"
 * /USD. The leading +/- makes direction explicit (0 carries no sign); cents are shown
 * only when present (up to 2 dp) so whole figures stay clean.
 */
export function formatCurrencyCode(value: number, currency: string): string {
  // Sub-cent band → an unsigned zero, keeping the sign consistent with the
  // displayed magnitude: a raw -0.003 otherwise signs off the un-rounded value
  // and prints "-0 USD" (reachable via FX multipliers on a clean 2dp figure —
  // LR-518 kvi review #2).
  const v = Math.abs(value) < 0.005 ? 0 : value;
  const sign = v > 0 ? '+' : v < 0 ? '-' : '';
  const abs = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(Math.abs(v));
  return `${sign}${abs} ${currency}`;
}

// ─── Phase 6: Submit price + dialog before→after delta ───────────────────────

/**
 * The single submit-price rule for an included MTM position:
 *   override ?? cob_price_raw ?? gross_price
 * (resolveDefaultPrice encodes the latter two: cob_price_raw ?? gross_price_raw).
 * Returned as a string, per the bulk_mark API Decimal contract.
 *
 * The dialog before/after totals (computeMtmDialogDelta) AND the marks[] payload
 * MUST both use this function so the count, the dialog "After" total, and the
 * submitted marks can never drift — all derive from one source of truth.
 */
export function getMtmSubmitPrice(
  position: TradeItem,
  override: string | undefined,
): string {
  return override ?? resolveDefaultPrice(position);
}

/**
 * Integer-cent margined value for a single row at the given price string.
 *   cents = quantity × price × (margin% / 100) × 100 = quantity × price × margin%
 * Computed as ONE float-multiply rounded ONCE to the nearest cent.
 *
 * We deliberately do NOT pre-round the price to cents. The old `Math.round(price * 100)`
 * dropped the price's 3rd/4th decimal (the API stores decimal_places=4) and amplified the
 * loss by quantity: 153.7575 × 1000 must be 153,757.50, not 153,760.00. A single multiply
 * also avoids the integer overflow a price-×10000 rescale would hit (qty × priceTenThou ×
 * marginHundredths can exceed Number.MAX_SAFE_INTEGER on large books); this form stays at
 * cents magnitude. Float drift on one product is < 1 ULP — far below the 0.5-cent Math.round
 * boundary — so the returned integer cents are exact and safe to ACCUMULATE across rows
 * (callers must still sum these integer cents, never raw dollar floats).
 */
function marginValueCents(
  quantity: number,
  priceStr: string,
  marginPct: number,
): number {
  return Math.round(quantity * Number(priceStr) * marginPct);
}

/**
 * P&L sign for an MTM mark of a given trade type. Mirrors backend `_direction()`
 * in `api/v1/mtm_runs/serializers.py` (LR-385): lend +1, borrow −1, repo /
 * reverse_repo 0 (repo is not repriced via MTM — collateral is handled by the
 * per-counterparty cash-pool margin call). Used to compute the Preview Gross Value Δ
 * column so the figure matches the History `delta_value` 1:1 on the same row —
 * Preview must read identically to History for the post-run confirmation.
 */
export function pnlDirection(tradeType: TradeItem['trade_type']): 1 | -1 | 0 {
  if (tradeType === 'lend') return 1;
  if (tradeType === 'borrow') return -1;
  return 0;
}

/**
 * Markable quantity of a position for MTM-value calculations — the SINGLE source
 * every margined-value helper routes through, so a pending partial recall/return is
 * applied in one place instead of at every cell that reads `quantity`.
 *
 * LR-388: returns the server-computed `remaining_quantity` (booked − pending recall)
 * when present, else the full booked `quantity`. So a borrow of 1,000 with a pending
 * 400 return marks 600 — matching the backend bulk_mark net and MTM history.
 */
export function effectiveQuantity(t: TradeItem): number {
  return t.remaining_quantity ?? t.quantity;
}

/**
 * Directioned market-value Δ for one row, in the position's currency:
 *   pnlDirection(trade_type) × (effective_price − baseline_price) × qty
 * Returns 0 for repo / reverse_repo (zero-direction). Mirrors backend
 * MtmRunMarkSerializer._delta_value so the Preview Gross Value Δ shown before the run
 * is the same number History will display after the run is committed.
 */
export function computeValueDelta(
  t: TradeItem,
  effectivePrice: number,
  baselinePrice: number,
): number {
  const raw = pnlDirection(t.trade_type) * (effectivePrice - baselinePrice) * effectiveQuantity(t);
  // Round once to whole cents. Two reasons: (a) strip IEEE-754 sub-cent noise
  // from the float multiplication — (100.001 - 100) × 1000 = 1.0000000000000018,
  // and the same noise would leak into strict `=== 1` comparisons or any
  // accumulator a caller built; (b) align with the backend, which quantizes the
  // Decimal result to cents (ROUND_HALF_UP, api/v1/mtm_runs/serializers.py:83).
  // Math.sign × round(|x|) gives HALF_UP for negatives too (vs Math.round which
  // is HALF_TO_+INF and would round -0.5 → 0 instead of -1).
  const cents = Math.sign(raw) * Math.round(Math.abs(raw) * 100);
  const result = cents / 100;
  // Normalise negative zero (e.g. (−1) × 0 × qty = −0) to +0. The UI handles −0
  // identically to 0 (signedDirection picks `+` for `value >= 0`), but call sites
  // doing `Object.is`-style strict comparisons would otherwise see −0 as not 0.
  return result === 0 ? 0 : result;
}

/**
 * Directioned collateral (margined) value Δ for one row, in the position's currency:
 *   pnlDirection(trade_type) × (effective_price − baseline_price) × qty × (margin / 100)
 * i.e. the Gross Value Δ scaled by this position's margin — the "margin price Δ × quantity"
 * the desk sees as the change in required collateral. Returns 0 for repo / reverse_repo
 * (zero-direction). `margin` is a PERCENT on TradeItem (e.g. 105 = 105%), so margin/100
 * is the margin_decimal. Mirrors backend MtmRunMarkSerializer.get_delta_collateral
 * (`delta_value × margin_decimal`, ROUND_HALF_UP — api/v1/mtm_runs/serializers.py:85-91),
 * so the Preview Margin Value Δ equals the History Margin Value Δ on the same row after the run.
 *
 * Rounds the full product ONCE to whole cents (NOT computeValueDelta() pre-rounded then
 * scaled — that would double-round). Same Math.sign × round(|x|) HALF_UP discipline as
 * computeValueDelta, so negatives quantize identically to the backend and −0 normalises to +0.
 */
export function computeCollateralDelta(
  t: TradeItem,
  effectivePrice: number,
  baselinePrice: number,
): number {
  const raw =
    pnlDirection(t.trade_type) *
    (effectivePrice - baselinePrice) *
    effectiveQuantity(t) *
    (t.margin / 100);
  const cents = Math.sign(raw) * Math.round(Math.abs(raw) * 100);
  const result = cents / 100;
  return result === 0 ? 0 : result;
}

// ─── LR-548: repo/reverse margin-call deltas (separate from the repo-neutral pnlDirection) ───

/** LR-548: resolved repo margin-call selection payload. */
export interface RepoCallSelection {
  /** ids to send as `position_ids`, or undefined to OMIT them (tolerant whole-book path). */
  positionIds: number[] | undefined;
  /** whether a post is allowed at all (needs ≥1 selected row). */
  canPost: boolean;
  /** whether the post carries explicit `position_ids` (a strict subset of the book). */
  isSubset: boolean;
}

/**
 * Decide the repo margin-call selection payload (LR-548). `position_ids` are OMITTED (the tolerant
 * whole-book path, which lets the trader acknowledge a partial COB skip) ONLY when the visible
 * selection is provably the ENTIRE eligible repo book: no eligible repo row hidden by a sidebar
 * filter (`visibleSelectableCount === bookSelectableCount`) AND every visible row still selected.
 * Otherwise the call is a strict subset of EXACTLY the visible-selected rows — so "all visible
 * selected" under an active filter never silently calls the hidden rows, and the "N of M" the trader
 * sees always matches what posts (WYSIWYG). A call needs ≥1 selected row, so `positionIds` is never
 * an empty array (which the backend would reject with a 400).
 *
 * `selectedIds` — visible repo rows still checked. `visibleSelectableCount` — eligible repo rows in
 * the sidebar-filtered view. `bookSelectableCount` — eligible repo rows in the whole (unfiltered)
 * book for the counterparty (what the omit-position_ids path would call).
 */
export function resolveRepoCallSelection(
  selectedIds: number[],
  visibleSelectableCount: number,
  bookSelectableCount: number,
): RepoCallSelection {
  const selectedCount = selectedIds.length;
  const canPost = selectedCount > 0;
  const useWholeBook =
    visibleSelectableCount > 0 &&
    selectedCount === visibleSelectableCount &&
    visibleSelectableCount === bookSelectableCount;
  const positionIds = !useWholeBook && canPost ? selectedIds : undefined;
  return { positionIds, canPost, isSubset: positionIds !== undefined };
}

/**
 * Exposure direction for a repo margin call — mirrors the backend `_EXPOSURE_DIRECTION`
 * (apps/positions/services/repo_margin_call.py): repo +1, reverse_repo −1, everything else 0.
 *
 * DISTINCT from pnlDirection, which stays repo-neutral (0) because repo is never MTM-repriced
 * (LR-385). The repo margin call values collateral off THIS direction, so the two repo delta
 * helpers below use it while pnlDirection is left untouched (LR-548 acceptance 8).
 */
export function repoExposureDirection(tradeType: TradeItem['trade_type']): 1 | -1 | 0 {
  if (tradeType === 'repo') return 1;
  if (tradeType === 'reverse_repo') return -1;
  return 0;
}

/**
 * Directioned GROSS value Δ for a repo/reverse row, in the position's currency:
 *   repoExposureDirection(trade_type) × (new_price − gross_price) × qty
 * The repo analogue of computeValueDelta, but measured from the fixed GROSS (booking) price — NOT
 * the moving MTM baseline — because repo is never repriced (no marks; LR-385). 0 for non-repo. Same
 * Math.sign × round(|x|) HALF_UP + −0-normalise discipline as computeValueDelta.
 */
export function computeRepoValueDelta(
  t: TradeItem,
  newPrice: number,
  grossPrice: number,
): number {
  const raw = repoExposureDirection(t.trade_type) * (newPrice - grossPrice) * effectiveQuantity(t);
  const cents = Math.sign(raw) * Math.round(Math.abs(raw) * 100);
  const result = cents / 100;
  return result === 0 ? 0 : result;
}

/**
 * Directioned MARGIN value Δ for a repo/reverse row — the signed per-line PRICE exposure the backend
 * returns as `clean_exposure` (apps/positions/services/repo_margin_call.py):
 *   direction × (market_value − trade_cash) ≡ direction × qty × (margin/100) × (new_price − gross_price)
 * since trade_cash = contract_value = qty × gross_price × (margin/100). Computed locally for grid
 * responsiveness; its SIGN must match the preview line's `clean_exposure` (LR-548 acceptance 5).
 *
 * LR-579: under Method B the line's `exposure` ALSO carries the unpaid accrued repo interest
 * (`accrued_component`), which is not derivable from the row — this column deliberately stays the
 * price move, and the accrued part is shown on the aggregate bar / post dialog / drill-down instead.
 *
 * `margin` is a PERCENT on TradeItem, so margin/100 = margin_decimal. Same HALF_UP-to-cents +
 * −0-normalise discipline as computeCollateralDelta. Repo has no partial recall, so
 * effectiveQuantity === quantity (== the backend's pos.quantity).
 */
export function computeRepoMarginValueDelta(
  t: TradeItem,
  newPrice: number,
  grossPrice: number,
): number {
  const raw =
    repoExposureDirection(t.trade_type) *
    (newPrice - grossPrice) *
    effectiveQuantity(t) *
    (t.margin / 100);
  const cents = Math.sign(raw) * Math.round(Math.abs(raw) * 100);
  const result = cents / 100;
  return result === 0 ? 0 : result;
}

/**
 * The MTM "before" baseline price for a position, as an API-original decimal string:
 * the latest committed mark if the position has ever been marked, else the gross
 * (booking) price. This is the single moving-baseline rule — after a run is committed
 * the new mark becomes latest_mark_price_raw, so the next run measures from it.
 *
 * latest_mark_price_raw is null ONLY when the position has never been marked (the
 * backend exposes the raw mark without the current_market_price fallback that
 * last_marked_price carries), so `?? gross_price_raw` is the genuine first-mark case.
 */
export function mtmBaselinePriceRaw(t: TradeItem): string {
  return t.latest_mark_price_raw ?? t.gross_price_raw;
}

/**
 * Integer-cent margined baseline value (quantity × baseline price × margin/100).
 * The single source of "before" for BOTH computeMtmExposure and computeMtmDialogDelta,
 * so the aggregate bar and the Run-MTM dialog cannot drift (closes the prior split where
 * exposure used contract_value while the dialog used gross_price_raw — different rounding).
 */
function baselineMarginedCents(t: TradeItem): number {
  // LR-388: effectiveQuantity = remaining markable shape (booked − pending recall). The baseline
  // MUST use the same quantity as the new margined value, else the Δ would mix 600 vs 1,000.
  return marginValueCents(effectiveQuantity(t), mtmBaselinePriceRaw(t), t.margin);
}

/**
 * The position's CURRENT (baseline) margined value in dollars — the margined value at the
 * moving baseline price (last mark, else booking). This is what a NEW margined value must be
 * compared against to colour a margin-direction signal (up = more collateral needed, down =
 * less). Exposed for the MtmTable "New Margin Value" cell so its green/red reflects this
 * baseline value, NOT `margin` (which is the margin PERCENT — comparing a value to a percent
 * rendered every row green). Same integer-cent source as computeMtmExposure / the dialog.
 */
export function baselineMarginedValue(t: TradeItem): number {
  return baselineMarginedCents(t) / 100;
}

export interface MtmDialogDelta {
  beforeCents: number;
  afterCents: number;
  deltaCents: number;
  before: number;
  after: number;
  delta: number;
}

/**
 * Compute the dialog before→after margined-value totals and their delta over
 * INCLUDED rows only (rows in excludedIds are omitted from both totals).
 *
 * Before = moving baseline (baselineMarginedCents: last marked value, else gross/booking)
 * — the SAME source as computeMtmExposure, so the dialog total equals the bar's signed net.
 * After = submit price = getMtmSubmitPrice(row, override). Delta is over MARGINED value.
 *
 * Repo/reverse_repo are NOT repriced via MTM (per-counterparty cash-pool call — LR-385) and
 * are SKIPPED here, mirroring computeMtmExposure — so this helper is self-protecting and can
 * never disagree with the aggregate bar even if a caller passes repo rows. Only lend/borrow
 * contribute; delta is direction-signed (lend +, borrow −) so After − Before equals the DUE
 * TO CP amount and matches the bar. beforeCents/afterCents stay as the unsigned totals.
 *
 * Float-safe: accumulate in integer cents, divide by 100 once at the end.
 * NEVER accumulate raw floats.
 */
export function computeMtmDialogDelta(
  rows: TradeItem[],
  priceOverrides: Map<number, string>,
  excludedIds: Set<number>,
): MtmDialogDelta {
  let beforeCents = 0;
  let afterCents = 0;
  let signedDeltaCents = 0;
  for (const r of rows) {
    if (excludedIds.has(r.id)) continue;
    // Skip repo/reverse_repo: not repriced via MTM (LR-385). Mirror computeMtmExposure so the
    // dialog total can never include a row the bar's signed net excludes (guard the full path).
    if (r.trade_type === 'repo' || r.trade_type === 'reverse_repo') continue;
    const b = baselineMarginedCents(r);
    const a = marginValueCents(
      effectiveQuantity(r),  // LR-388: remaining markable shape (booked − pending recall)
      getMtmSubmitPrice(r, priceOverrides.get(r.id)),
      r.margin,
    );
    beforeCents += b;
    afterCents += a;
    // Only lend/borrow reach here; borrow negates so After−Before equals the signed net.
    const dir = r.trade_type === 'borrow' ? -1 : 1;
    signedDeltaCents += dir * (a - b);
  }
  const deltaCents = signedDeltaCents;
  return {
    beforeCents,
    afterCents,
    deltaCents,
    before: beforeCents / 100,
    after: afterCents / 100,
    delta: deltaCents / 100,
  };
}
